import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import { 
  ScaleIcon,
  ExclamationCircleIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline';

const UnitForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    unit_name: '',
    display_label: '',
    abbreviation: '',
    description: '',
    sort_order: 1,
    is_active: true
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    if (isEditing) {
      fetchUnit();
    }
  }, [id]);

  const fetchUnit = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/price-book/units/${id}`);
      const unit = response.data;
      
      setFormData({
        unit_name: unit.unit_name || '',
        display_label: unit.display_label || '',
        abbreviation: unit.abbreviation || '',
        description: unit.description || '',
        sort_order: unit.sort_order || 1,
        is_active: unit.is_active !== false
      });
    } catch (err) {
      console.error('Error fetching unit:', err);
      setError('Failed to load unit data');
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.unit_name.trim()) {
      errors.unit_name = 'Unit name is required';
    }

    if (!formData.display_label.trim()) {
      errors.display_label = 'Display label is required';
    }

    if (formData.sort_order < 1) {
      errors.sort_order = 'Sort order must be at least 1';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const submitData = {
        ...formData,
        sort_order: parseInt(formData.sort_order),
        abbreviation: formData.abbreviation || null
      };

      if (isEditing) {
        await api.put(`/price-book/units/${id}`, submitData);
      } else {
        await api.post('/price-book/units', submitData);
      }

      navigate('/price-book/units');
    } catch (err) {
      console.error('Error saving unit:', err);
      setError(err.response?.data?.error || 'Failed to save unit');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  if (loading && isEditing) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center">
          <button
            onClick={() => navigate('/price-book/units')}
            className="mr-4 p-2 text-gray-400 hover:text-gray-600"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEditing ? 'Edit Unit of Measure' : 'Add New Unit of Measure'}
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              {isEditing ? 'Update unit information' : 'Create a new unit of measure'}
            </p>
          </div>
        </div>
      </div>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <ExclamationCircleIcon className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">{error}</h3>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      <div className="bg-white shadow rounded-lg">
        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            {/* Unit Name */}
            <div>
              <label htmlFor="unit_name" className="block text-sm font-medium text-gray-700">
                Unit Name *
              </label>
              <input
                type="text"
                name="unit_name"
                id="unit_name"
                value={formData.unit_name}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.unit_name ? 'border-red-300' : ''
                }`}
                placeholder="e.g., hour, each, project"
              />
              {validationErrors.unit_name && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.unit_name}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Internal name used for system identification (lowercase, no spaces)
              </p>
            </div>

            {/* Display Label */}
            <div>
              <label htmlFor="display_label" className="block text-sm font-medium text-gray-700">
                Display Label *
              </label>
              <input
                type="text"
                name="display_label"
                id="display_label"
                value={formData.display_label}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.display_label ? 'border-red-300' : ''
                }`}
                placeholder="e.g., Hour, Each, Project"
              />
              {validationErrors.display_label && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.display_label}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                User-friendly name displayed in the interface
              </p>
            </div>

            {/* Abbreviation */}
            <div>
              <label htmlFor="abbreviation" className="block text-sm font-medium text-gray-700">
                Abbreviation
              </label>
              <input
                type="text"
                name="abbreviation"
                id="abbreviation"
                value={formData.abbreviation}
                onChange={handleChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                placeholder="e.g., hr, ea, proj"
                maxLength="10"
              />
              <p className="mt-1 text-xs text-gray-500">
                Optional short form (max 10 characters)
              </p>
            </div>

            {/* Sort Order */}
            <div>
              <label htmlFor="sort_order" className="block text-sm font-medium text-gray-700">
                Sort Order
              </label>
              <input
                type="number"
                name="sort_order"
                id="sort_order"
                min="1"
                value={formData.sort_order}
                onChange={handleChange}
                className={`mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm ${
                  validationErrors.sort_order ? 'border-red-300' : ''
                }`}
              />
              {validationErrors.sort_order && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.sort_order}</p>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Controls the display order (lower numbers appear first)
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              name="description"
              id="description"
              rows={3}
              value={formData.description}
              onChange={handleChange}
              className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              placeholder="Optional description of this unit of measure..."
            />
            <p className="mt-1 text-xs text-gray-500">
              Optional description to help users understand this unit
            </p>
          </div>

          {/* Active Status */}
          <div className="flex items-center">
            <input
              id="is_active"
              name="is_active"
              type="checkbox"
              checked={formData.is_active}
              onChange={handleChange}
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
            />
            <label htmlFor="is_active" className="ml-2 block text-sm text-gray-900">
              Active
            </label>
            <p className="ml-2 text-xs text-gray-500">
              Inactive units are hidden from selection lists
            </p>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={() => navigate('/price-book/units')}
              className="bg-white py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                  {isEditing ? 'Updating...' : 'Creating...'}
                </>
              ) : (
                <>
                  <ScaleIcon className="h-4 w-4 mr-2" />
                  {isEditing ? 'Update Unit' : 'Create Unit'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnitForm; 